import { useEffect, useState } from "react";
import { api, mensajeError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useOpciones } from "../hooks/useOpciones";
import { PageHeader } from "../components/ui/PageHeader";
import { Spinner } from "../components/ui/Spinner";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { EmptyState } from "../components/ui/EmptyState";
import { FileText, Plus, Download, Eye, XCircle, AlertTriangle, Settings } from "lucide-react";
import { Link } from "react-router-dom";

interface Catalogo {
  value: string;
  label: string;
}

interface Catalogos {
  regimenesFiscales: Catalogo[];
  usosCfdi: Catalogo[];
  formasPago: Catalogo[];
  metodosPago: Catalogo[];
  monedas: Catalogo[];
  motivosCancelacion: Catalogo[];
  facturamaConfigurado: boolean;
  datosEmisor: { rfc: string | null; razonSocial: string | null; regimenFiscal: string | null; codigoPostal: string | null };
  csd: { estado: string; errorMensaje: string | null };
  limiteTimbres: { usadosEsteMes: number; limiteMensual: number; extra: number; disponibles: number; alcanzado: boolean };
}

function descargarBlob(blob: Blob, nombreArchivo: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombreArchivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function Facturacion() {
  const { soloLectura } = useAuth();
  // Solo se pueden facturar ventas pagadas que todavía no tengan una factura.
  const {
    opciones: ventasFacturables,
    raw: ventasRaw,
    cargando: cargandoVentas,
  } = useOpciones(
    "/ventas",
    (v) => `Venta ${v.folio ? `#${v.folio}` : `#${v.id.slice(0, 6)}`} · ${v.cliente?.nombre ?? "Público en general"} · $${v.total.toLocaleString("es-MX")}`,
    (v) => v.estado === "PAGADA" && !v.factura
  );

  const [facturas, setFacturas] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [catalogos, setCatalogos] = useState<Catalogos | null>(null);

  const [modalAbierto, setModalAbierto] = useState(false);
  const [ventaId, setVentaId] = useState("");
  const [usoCFDI, setUsoCFDI] = useState("G03");
  const [formaPago, setFormaPago] = useState("01");
  const [metodoPago, setMetodoPago] = useState<"PUE" | "PPD">("PUE");
  const [moneda, setMoneda] = useState("MXN");
  const [rfcReceptor, setRfcReceptor] = useState("");
  const [nombreReceptor, setNombreReceptor] = useState("");
  const [regimenFiscalReceptor, setRegimenFiscalReceptor] = useState("");
  const [codigoPostalReceptor, setCodigoPostalReceptor] = useState("");
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalCancelar, setModalCancelar] = useState<any | null>(null);
  const [motivoCancelacion, setMotivoCancelacion] = useState("02");
  const [folioSustitucion, setFolioSustitucion] = useState("");
  const [cancelando, setCancelando] = useState(false);
  const [errorCancelar, setErrorCancelar] = useState<string | null>(null);

  const ventaSeleccionada = ventasRaw.find((v) => v.id === ventaId);
  const clienteFacturable =
    !!ventaSeleccionada?.cliente?.rfc && !!ventaSeleccionada?.cliente?.regimenFiscal && !!ventaSeleccionada?.cliente?.codigoPostal;

  // Al elegir la venta, se precargan los datos que YA se tenían capturados
  // del cliente (RFC, nombre, régimen fiscal, código postal) — antes se
  // dejaban siempre en blanco y había que volver a escribir a mano incluso
  // lo que el cliente ya tenía guardado.
  useEffect(() => {
    const cliente = ventaSeleccionada?.cliente;
    setRfcReceptor(cliente?.rfc ?? "");
    setNombreReceptor(cliente?.nombre ?? "");
    setRegimenFiscalReceptor(cliente?.regimenFiscal ?? "");
    setCodigoPostalReceptor(cliente?.codigoPostal ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ventaId]);

  async function cargar() {
    setCargando(true);
    try {
      const [resFacturas, resCatalogos] = await Promise.all([api.get("/facturacion"), api.get("/facturacion/catalogos")]);
      setFacturas(resFacturas.data.items ?? resFacturas.data);
      setCatalogos(resCatalogos.data);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  function abrirGenerar() {
    setVentaId("");
    setUsoCFDI("G03");
    setFormaPago("01");
    setMetodoPago("PUE");
    setMoneda("MXN");
    setRfcReceptor("");
    setNombreReceptor("");
    setRegimenFiscalReceptor("");
    setCodigoPostalReceptor("");
    setError(null);
    setModalAbierto(true);
  }

  async function generarFactura() {
    if (soloLectura || !ventaId) return;
    setProcesando(true);
    setError(null);
    try {
      await api.post("/facturacion/generar", {
        ventaId,
        usoCFDI,
        formaPago,
        metodoPago,
        moneda,
        ...(clienteFacturable
          ? {}
          : {
              rfcReceptor: rfcReceptor || undefined,
              nombreReceptor: nombreReceptor || undefined,
              regimenFiscalReceptor: regimenFiscalReceptor || undefined,
              codigoPostalReceptor: codigoPostalReceptor || undefined,
            }),
      });
      setModalAbierto(false);
      cargar();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setProcesando(false);
    }
  }

  function abrirCancelar(factura: any) {
    setModalCancelar(factura);
    setMotivoCancelacion("02");
    setFolioSustitucion("");
    setErrorCancelar(null);
  }

  async function confirmarCancelar() {
    if (!modalCancelar) return;
    setCancelando(true);
    setErrorCancelar(null);
    try {
      await api.post(`/facturacion/${modalCancelar.id}/cancelar`, {
        motivo: motivoCancelacion,
        folioSustitucion: motivoCancelacion === "01" ? folioSustitucion : undefined,
      });
      setModalCancelar(null);
      cargar();
    } catch (err) {
      setErrorCancelar(mensajeError(err));
    } finally {
      setCancelando(false);
    }
  }

  async function verPdf(factura: any) {
    try {
      const res = await api.get(`/facturacion/${factura.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (err) {
      alert(mensajeError(err));
    }
  }

  async function descargarXml(factura: any) {
    try {
      const res = await api.get(`/facturacion/${factura.id}/xml`, { responseType: "blob" });
      descargarBlob(res.data, `factura-${factura.serie ?? ""}${factura.folio ?? factura.id}.xml`);
    } catch (err) {
      alert(mensajeError(err));
    }
  }

  const csdListo = catalogos?.csd.estado === "VALIDO";
  const limiteAlcanzado = !!catalogos?.limiteTimbres.alcanzado;

  return (
    <div>
      <PageHeader
        titulo="Facturación CFDI"
        descripcion="Genera y timbra facturas electrónicas (CFDI 4.0) para tus ventas."
        icono={<FileText size={20} />}
        acciones={
          !soloLectura ? (
            <button className="btn-primary" disabled={!csdListo || limiteAlcanzado} onClick={abrirGenerar}>
              <Plus size={16} /> Generar factura
            </button>
          ) : undefined
        }
      />

      {catalogos && (
        <div className="card mb-4 p-4">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-medium text-slate-600">Timbres usados este mes</span>
            <span className="text-slate-400">
              {catalogos.limiteTimbres.usadosEsteMes} de {catalogos.limiteTimbres.limiteMensual}
              {catalogos.limiteTimbres.extra > 0 && ` + ${catalogos.limiteTimbres.extra} extra`}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className={`h-full rounded-full transition-all ${limiteAlcanzado ? "bg-red-500" : "bg-teal-500"}`}
              style={{
                width: `${Math.min(
                  100,
                  (catalogos.limiteTimbres.usadosEsteMes / Math.max(1, catalogos.limiteTimbres.limiteMensual + catalogos.limiteTimbres.extra)) * 100
                )}%`,
              }}
            />
          </div>
        </div>
      )}

      {limiteAlcanzado && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <p>
              Ya usaste todos tus timbres disponibles de este mes. Contacta a soporte para comprar más timbres y
              seguir facturando sin esperar al siguiente mes.
            </p>
          </div>
          <a
            href="mailto:soporte@mascotas.vet?subject=Comprar%20m%C3%A1s%20timbres%20para%20facturar"
            className="btn-secondary !bg-white whitespace-nowrap"
          >
            Contactar soporte
          </a>
        </div>
      )}

      {catalogos && !catalogos.facturamaConfigurado && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <p>El proveedor de timbrado todavía no está configurado en el servidor. Contacta a soporte@mascotas.vet.</p>
        </div>
      )}

      {catalogos && catalogos.facturamaConfigurado && !csdListo && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <p>
              Todavía no has cargado tu certificado de sello digital (CSD). Configúralo para poder empezar a
              timbrar facturas.
            </p>
          </div>
          <Link to="/configuracion" className="btn-secondary !bg-white whitespace-nowrap">
            <Settings size={15} /> Ir a Configuración
          </Link>
        </div>
      )}

      {cargando ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : facturas.length === 0 ? (
        <EmptyState
          titulo="No hay facturas generadas todavía"
          descripcion='Genera la primera con el botón "Generar factura", a partir de una venta ya pagada.'
        />
      ) : (
        <div className="card divide-y divide-slate-50">
          {facturas.map((f) => (
            <div key={f.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <FileText className="text-teal-500 shrink-0" size={20} />
                <div>
                  <p className="font-medium text-slate-700">{f.nombreReceptor ?? f.cliente?.nombre ?? "Público en general"}</p>
                  <p className="text-xs text-slate-400">
                    {f.serie ?? ""}{f.folio ?? "-"} · ${f.venta?.total?.toLocaleString("es-MX") ?? ""}
                    {f.uuid && <> · UUID {f.uuid.slice(0, 8)}...</>}
                  </p>
                  {f.estado === "ERROR" && (
                    <p className="mt-1 text-xs text-red-600">
                      {f.proveedorRespuesta?.mensaje || f.proveedorRespuesta?.Message || "No se pudo timbrar."}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge color={f.estado === "TIMBRADA" ? "verde" : f.estado === "CANCELADA" ? "rojo" : "amarillo"}>
                  {f.estado === "TIMBRADA" ? "Timbrada" : f.estado === "CANCELADA" ? "Cancelada" : f.estado === "ERROR" ? "Error" : "Pendiente"}
                </Badge>
                {f.estado === "TIMBRADA" && (
                  <>
                    <button className="btn-ghost !px-2" title="Ver PDF" onClick={() => verPdf(f)}>
                      <Eye size={16} />
                    </button>
                    <button className="btn-ghost !px-2" title="Descargar XML" onClick={() => descargarXml(f)}>
                      <Download size={16} />
                    </button>
                    {!soloLectura && (
                      <button className="btn-ghost !px-2 text-red-500" title="Cancelar factura" onClick={() => abrirCancelar(f)}>
                        <XCircle size={16} />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modalAbierto} onClose={() => setModalAbierto(false)} title="Generar factura">
        <div className="space-y-4">
          <div>
            <label className="label">Venta a facturar</label>
            <select className="input" value={ventaId} onChange={(e) => setVentaId(e.target.value)}>
              <option value="">Selecciona...</option>
              {ventasFacturables.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {!cargandoVentas && ventasFacturables.length === 0 && (
              <p className="mt-1 text-xs text-slate-400">No hay ventas pagadas pendientes de facturar.</p>
            )}
          </div>

          {ventaSeleccionada && !clienteFacturable && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
              {ventaSeleccionada.cliente
                ? "Este cliente no tiene RFC, régimen fiscal o código postal capturado. Complétalo aquí solo para esta factura, o déjalo en blanco para facturar a Público en general."
                : "Esta venta no tiene un cliente vinculado — se facturará a Público en general, a menos que captures un RFC aquí."}
            </div>
          )}

          {ventaSeleccionada && !clienteFacturable && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">RFC del receptor (opcional)</label>
                <input className="input uppercase" value={rfcReceptor} onChange={(e) => setRfcReceptor(e.target.value.toUpperCase())} />
              </div>
              <div>
                <label className="label">Nombre / razón social</label>
                <input className="input" value={nombreReceptor} onChange={(e) => setNombreReceptor(e.target.value)} />
              </div>
              <div>
                <label className="label">Régimen fiscal</label>
                <select className="input" value={regimenFiscalReceptor} onChange={(e) => setRegimenFiscalReceptor(e.target.value)}>
                  <option value="">Selecciona...</option>
                  {(catalogos?.regimenesFiscales ?? []).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Código postal</label>
                <input className="input" maxLength={5} value={codigoPostalReceptor} onChange={(e) => setCodigoPostalReceptor(e.target.value.replace(/\D/g, "").slice(0, 5))} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Uso de CFDI</label>
              <select className="input" value={usoCFDI} onChange={(e) => setUsoCFDI(e.target.value)}>
                {(catalogos?.usosCfdi ?? []).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Moneda</label>
              <select className="input" value={moneda} onChange={(e) => setMoneda(e.target.value)}>
                {(catalogos?.monedas ?? []).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Forma de pago</label>
              <select className="input" value={formaPago} onChange={(e) => setFormaPago(e.target.value)}>
                {(catalogos?.formasPago ?? []).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Método de pago</label>
              <select className="input" value={metodoPago} onChange={(e) => setMetodoPago(e.target.value as "PUE" | "PPD")}>
                {(catalogos?.metodosPago ?? []).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {limiteAlcanzado && (
            <p className="text-sm text-red-600">
              Ya usaste todos tus timbres disponibles de este mes. Contacta a soporte@mascotas.vet para comprar más.
            </p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setModalAbierto(false)}>Cancelar</button>
            <button className="btn-primary" disabled={!ventaId || procesando || limiteAlcanzado} onClick={generarFactura}>
              {procesando ? "Timbrando..." : "Timbrar factura"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={!!modalCancelar} onClose={() => setModalCancelar(null)} title="Cancelar factura">
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            Esta acción se manda directamente al SAT. Según el motivo, es posible que el cliente tenga que aceptar la
            cancelación (hasta 72 horas) antes de que quede cancelada de verdad.
          </p>
          <div>
            <label className="label">Motivo de cancelación</label>
            <select className="input" value={motivoCancelacion} onChange={(e) => setMotivoCancelacion(e.target.value)}>
              {(catalogos?.motivosCancelacion ?? []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          {motivoCancelacion === "01" && (
            <div>
              <label className="label">UUID de la factura que sustituye a esta</label>
              <input className="input" value={folioSustitucion} onChange={(e) => setFolioSustitucion(e.target.value)} />
            </div>
          )}
          {errorCancelar && <p className="text-sm text-red-600">{errorCancelar}</p>}
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setModalCancelar(null)}>Volver</button>
            <button
              className="btn-primary bg-red-600 hover:bg-red-700"
              disabled={cancelando || (motivoCancelacion === "01" && !folioSustitucion)}
              onClick={confirmarCancelar}
            >
              {cancelando ? "Cancelando..." : "Sí, cancelar factura"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
